import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDateFr = (iso) =>
  new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const getGeo = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      () => resolve({}),
      { timeout: 5000 }
    );
  });

// ─── Step Indicator ───────────────────────────────────────────────────────────

const STEPS = ['Identité', 'Signature', 'Confirmation'];

const StepIndicator = ({ current }) => (
  <div className="flex items-center mb-8">
    {STEPS.map((label, idx) => {
      const step = idx + 1;
      const isDone = step < current;
      const isActive = step === current;
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors duration-200 ${
                isDone
                  ? 'bg-primary-100 text-primary-600'
                  : isActive
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {isDone ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              ) : (
                step
              )}
            </div>
            <span
              className={`mt-1 text-xs font-medium ${
                isActive ? 'text-primary-600' : isDone ? 'text-primary-400' : 'text-gray-400'
              }`}
            >
              {label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-2 mb-4 ${
                step < current ? 'bg-primary-200' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Upload Zone ──────────────────────────────────────────────────────────────

const UploadZone = ({ label, accept, file, onChange, error }) => {
  const inputRef = useRef(null);
  return (
    <div>
      <label className="form-label">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="mt-1 flex flex-col items-center justify-center px-4 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors duration-200"
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={onChange}
        />
        <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
        {file ? (
          <p className="text-sm text-primary-600 font-medium text-center">{file.name}</p>
        ) : (
          <p className="text-xs text-gray-500 text-center">Appuyez pour sélectionner</p>
        )}
        <p className="text-xs text-gray-400 mt-1">PNG, JPG · max 20 Mo</p>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SignDischarge = () => {
  const { signToken } = useParams();

  // ── Session state
  const [pageState, setPageState] = useState('loading'); // loading | error | already_signed | form | success
  const [session, setSession] = useState(null);
  const [organizationHint, setOrganizationHint] = useState('');
  const [errorReason, setErrorReason] = useState('');

  // ── Stepper
  const [step, setStep] = useState(1);

  // ── Step 1 fields
  const [signerName, setSignerName] = useState('');
  const [signerFunction, setSignerFunction] = useState('');
  const [signerOrganization, setSignerOrganization] = useState('');

  // ── Signer type
  const [signerType, setSignerType] = useState('beneficiaire');

  // ── Step 2 state
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [uploadedStamp, setUploadedStamp] = useState(null);
  const [sigError, setSigError] = useState('');
  const [stampError, setStampError] = useState('');

  // ── Step 3 state
  const [certChecked, setCertChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Success state
  const [signedAt, setSignedAt] = useState(null);

  // ── Unmount safety ref
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  // ── Fetch session on mount
  useEffect(() => {
    axios
      .get(`/api/sign/${signToken}`)
      .then((res) => {
        const { valid, session: sess, organizationHint: hint, reason } = res.data;
        if (!valid) {
          if (reason === 'already_used') {
            setPageState('already_signed');
          } else {
            setErrorReason(reason || 'invalid');
            setPageState('error');
          }
        } else {
          setSession(sess);
          setSignerType(res.data.session?.signerType || 'beneficiaire');
          setOrganizationHint(hint || '');
          setSignerOrganization(hint || '');
          setPageState('form');
        }
      })
      .catch(() => {
        setErrorReason('invalid');
        setPageState('error');
      });
  }, [signToken]);

  // ── Step 1 → 2
  const step1Valid = signerName.trim() && signerFunction.trim() && signerOrganization.trim();

  const goToStep2 = () => {
    setStep(2);
  };

  // ── File validation
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 Mo

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // Allow re-selecting the same file
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setSigError('Fichier trop volumineux. Maximum 20 Mo.');
      return;
    }
    setSigError('');
    setUploadedSignature(file);
  };

  const handleStampUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // Allow re-selecting the same file
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setStampError('Fichier trop volumineux. Maximum 20 Mo.');
      return;
    }
    setStampError('');
    setUploadedStamp(file);
  };

  // ── Submit
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      const geo = await getGeo();

      if (!isMountedRef.current) return;

      const formData = new FormData();
      formData.append('signerName', signerName.trim());
      formData.append('signerFunction', signerFunction.trim());
      formData.append('signerOrganization', signerOrganization.trim());
      formData.append('certificationChecked', 'true');
      formData.append('geo', JSON.stringify(geo));

      if (uploadedSignature) {
        formData.append('signature', uploadedSignature);
      }

      if (uploadedStamp) {
        formData.append('stamp', uploadedStamp);
      }

      await axios.post(`/api/sign/${signToken}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!isMountedRef.current) return;
      setSignedAt(new Date().toISOString());
      setPageState('success');
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err.response?.status === 409) {
        setPageState('already_signed');
      } else {
        setSubmitError(
          err.response?.data?.error ||
            'Une erreur est survenue. Veuillez réessayer.'
        );
      }
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  }, [signToken, signerName, signerFunction, signerOrganization, uploadedSignature, uploadedStamp, signerType]);

  // ─── Render: Loading ──────────────────────────────────────────────────────

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Chargement en cours...</p>
        </div>
      </div>
    );
  }

  // ─── Render: Error ────────────────────────────────────────────────────────

  if (pageState === 'error') {
    const messages = {
      expired:
        "Ce lien de signature a expiré. Veuillez contacter l'animateur pour en obtenir un nouveau.",
      already_used: 'Cette décharge a déjà été signée.',
      replaced: "Lien de signature invalide. Veuillez contacter l'animateur.",
      invalid: "Lien de signature invalide. Veuillez contacter l'animateur.",
    };
    const msg = messages[errorReason] || messages.invalid;

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-6">
            <img src="/assets/logo.webp" alt="Tchin-Lait" className="mx-auto h-16 w-auto mb-4" />
          </div>
          <div className="glassmorphism p-6 rounded-xl text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Lien invalide</h2>
            <p className="text-sm text-gray-600">{msg}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Already signed ───────────────────────────────────────────────

  if (pageState === 'already_signed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-6">
            <img src="/assets/logo.webp" alt="Tchin-Lait" className="mx-auto h-16 w-auto mb-4" />
          </div>
          <div className="glassmorphism p-6 rounded-xl text-center">
            <div className="w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Déjà signé</h2>
            <p className="text-sm text-gray-600">Cette décharge a déjà été signée.</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Success ──────────────────────────────────────────────────────

  if (pageState === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-6">
            <img src="/assets/logo.webp" alt="Tchin-Lait" className="mx-auto h-16 w-auto mb-4" />
          </div>
          <div className="glassmorphism p-6 rounded-xl text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-9 h-9 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Signature enregistrée</h2>
            <p className="text-sm text-gray-600 mb-4">
              Merci, <span className="font-medium">{signerName}</span>. Votre signature a bien été reçue.
            </p>
            {signedAt && (
              <p className="text-xs text-gray-400">
                Signé le {formatDateFr(signedAt)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Form ─────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Logo + title */}
        <div className="text-center mb-6">
          <img src="/assets/logo.webp" alt="Tchin-Lait" className="mx-auto h-16 w-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">
            {signerType === 'distributeur' ? 'Signature de décharge' : 'Confirmation de réception'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {signerType === 'distributeur'
              ? 'Confirmez la remise des produits en signant cette décharge'
              : 'Confirmez la réception des produits en signant ce document'}
          </p>
        </div>

        <div className="glassmorphism p-6 rounded-xl">
          <StepIndicator current={step} />

          {/* ── STEP 1: Identité ────────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Votre identité</h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="signerName" className="form-label">
                    Prénom et Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="signerName"
                    type="text"
                    className="form-input"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="ex: Ahmed Benali"
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label htmlFor="signerFunction" className="form-label">
                    Fonction / Qualité <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="signerFunction"
                    type="text"
                    className="form-input"
                    value={signerFunction}
                    onChange={(e) => setSignerFunction(e.target.value)}
                    placeholder="ex: Gérant, Responsable réception"
                  />
                </div>

                <div>
                  <label htmlFor="signerOrganization" className="form-label">
                    Organisation <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="signerOrganization"
                    type="text"
                    className="form-input"
                    value={signerOrganization}
                    onChange={(e) => setSignerOrganization(e.target.value)}
                    placeholder="Nom de votre organisation"
                  />
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  disabled={!step1Valid}
                  onClick={goToStep2}
                  className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Signature ────────────────────────────────────────── */}
          {step === 2 && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                {signerType === 'distributeur' ? 'Signature et cachet' : 'Votre signature'}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {signerType === 'distributeur'
                  ? 'Importez une image de votre signature et de votre cachet officiel.'
                  : 'Importez une image de votre signature.'}
              </p>
              <div className="space-y-4">
                <UploadZone
                  label="Signature (obligatoire)"
                  accept="image/png,image/jpeg"
                  file={uploadedSignature}
                  onChange={handleSignatureUpload}
                  error={sigError}
                />
                <UploadZone
                  label="Cachet (optionnel)"
                  accept="image/png,image/jpeg"
                  file={uploadedStamp}
                  onChange={handleStampUpload}
                  error={stampError}
                />
              </div>
              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  disabled={!uploadedSignature}
                  onClick={() => setStep(3)}
                  className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
                <button type="button" onClick={() => setStep(1)}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  Retour
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Confirmation ─────────────────────────────────────── */}
          {step === 3 && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Confirmation</h2>

              {/* Summary card */}
              <div className="bg-gray-50 rounded-lg p-4 mb-5 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Signataire</span>
                  <span className="font-medium text-gray-900">{signerName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Fonction</span>
                  <span className="text-gray-700">{signerFunction}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Organisation</span>
                  <span className="text-gray-700">{signerOrganization}</span>
                </div>
                {session?.signatureSessionId && (
                  <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                    <span className="text-gray-500">Référence</span>
                    <span className="font-mono text-xs text-gray-500 truncate max-w-[140px]">
                      {String(session.signatureSessionId).slice(0, 12)}…
                    </span>
                  </div>
                )}
                {session?.expiresAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Expire le</span>
                    <span className="text-gray-700">{formatDateFr(session.expiresAt)}</span>
                  </div>
                )}
              </div>

              {/* Certification checkbox */}
              <label className="flex items-start gap-3 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={certChecked}
                  onChange={(e) => setCertChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  {signerType === 'distributeur'
                    ? "Je certifie avoir remis les produits listés dans cette décharge à l'animateur désigné"
                    : "Je certifie avoir reçu les produits listés dans ce document d'attribution"}
                </span>
              </label>

              {submitError && (
                <p className="mb-4 text-sm text-red-600">{submitError}</p>
              )}

              <div className="space-y-3">
                <button
                  type="button"
                  disabled={!certChecked || submitting}
                  onClick={handleSubmit}
                  className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Signer et confirmer'
                  )}
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setStep(2)}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-40"
                >
                  Retour
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignDischarge;
